import React, { useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import CloseButton from "../../atoms/CloseButton";
import NavArrowButton from "../../atoms/NavArrowButton";
import "./index.scss";

/**
 * Reusable ImageLightbox organism
 * - Variants: 'modal' (default) centers image within card, 'fullscreen' covers viewport
 * - Tone: 'dark' (default) or 'light' for backdrop and surface accents
 * - Navigation: show prev/next controls if onPrev/onNext provided
 */
export default function ImageLightbox({
  isOpen = false,
  src,
  alt,
  caption,
  items = [],
  activeIndex = 0,
  variant = "modal",
  tone = "dark",
  onClose,
  onPrev,
  onNext,
  onSelect,
}) {
  const { t } = useTranslation();
  const thumbnailItems = useMemo(
    () => (Array.isArray(items) ? items.filter((item) => item && item.src) : []),
    [items],
  );
  const hasThumbnails = thumbnailItems.length > 1 && typeof onSelect === 'function';
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose?.();
    } else if (e.key === 'ArrowLeft') {
      onPrev?.();
    } else if (e.key === 'ArrowRight') {
      onNext?.();
    }
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const classNames = useMemo(() => {
    const v = (variant || 'modal').toLowerCase();
    const theme = (tone || 'dark').toLowerCase();
    return `image-lightbox image-lightbox--${v} image-lightbox--${theme}${hasThumbnails ? ' image-lightbox--with-thumbnails' : ''}`;
  }, [hasThumbnails, variant, tone]);

  if (!isOpen) return null;

  return (
    <div className={classNames} role="dialog" aria-modal="true" aria-label={t('imageLightbox.aria.dialog')}>
      <button type="button" className="image-lightbox__backdrop" aria-label={t('actions.close')} onClick={onClose} />
      <div className="image-lightbox__content">
        <div className="image-lightbox__stage">
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img src={src} alt={alt || t('imageLightbox.alt.preview')} className="image-lightbox__image" />
          {caption ? <div className="image-lightbox__caption">{caption}</div> : null}
          <CloseButton className="image-lightbox__close" ariaLabel={t('actions.close')} onClick={onClose} />
          {(typeof onPrev === 'function') && (
            <NavArrowButton
              direction="previous"
              className="image-lightbox__nav image-lightbox__nav--prev"
              ariaLabel={t('imageLightbox.aria.previous')}
              onClick={onPrev}
            />
          )}
          {(typeof onNext === 'function') && (
            <NavArrowButton
              direction="next"
              className="image-lightbox__nav image-lightbox__nav--next"
              ariaLabel={t('imageLightbox.aria.next')}
              onClick={onNext}
            />
          )}
        </div>
        {hasThumbnails ? (
          <div className="image-lightbox__thumb-strip" aria-label={t('imageLightbox.aria.thumbnails')}>
            {thumbnailItems.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  type="button"
                  key={`${item.src}-${index}`}
                  className={`image-lightbox__thumb${isActive ? ' is-active' : ''}`}
                  onClick={() => onSelect(index)}
                  aria-label={t('imageLightbox.aria.thumbnail', { index: index + 1 })}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <img src={item.thumb || item.src} alt="" loading="lazy" />
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

ImageLightbox.propTypes = {
  isOpen: PropTypes.bool,
  src: PropTypes.string,
  alt: PropTypes.string,
  caption: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string.isRequired,
    thumb: PropTypes.string,
    alt: PropTypes.string,
  })),
  activeIndex: PropTypes.number,
  variant: PropTypes.oneOf(["modal", "fullscreen"]),
  tone: PropTypes.oneOf(["dark", "light"]),
  onClose: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onSelect: PropTypes.func,
};
